import { CONSENT_VERSION } from './consent';

export const CONSENT_EXPERIMENT_ID = 'consent_copy_v1';
export const CONSENT_EXPERIMENT_STORAGE_KEY = 'lvm_consent_experiment_v1';
export const CONSENT_MEASUREMENT_CHOICE_KEY = 'lvm_consent_measurement_v1';
export const CONSENT_VISIT_KEY = 'lvm_consent_visit_v1';
export const CONSENT_DISMISSED_KEY = 'lvm_consent_dismissed_v1';
export const CONSENT_EXPERIMENT_MAX_AGE_MS = 35 * 24 * 60 * 60 * 1000;
export const CONSENT_VARIANTS = ['clear_choice', 'benefit_clarity'];

const ENDPOINT = '/api/analytics/consent-funnel';
let memoryVisitId = '';
let memoryVariant = '';
let memoryVariantExpiresAt = 0;
let memoryMeasurementChoice = '';
const memoryRecordedActions = new Set();

const createToken = (prefix) => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}_${crypto.randomUUID()}`;
  }
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 14)}`;
};

const readStorage = (storage, key) => {
  try { return storage.getItem(key); } catch (error) { return null; }
};

const writeStorage = (storage, key, value) => {
  try {
    storage.setItem(key, value);
    return true;
  } catch (error) {
    return false;
  }
};

const removeStorage = (storage, key) => {
  try { storage.removeItem(key); } catch (error) { /* storage unavailable */ }
};

export const hasConsentMeasurementObjection = () => {
  if (typeof window === 'undefined') return false;
  return (readStorage(window.localStorage, CONSENT_MEASUREMENT_CHOICE_KEY) || memoryMeasurementChoice) === 'object';
};

const getVisitId = () => {
  const visitKey = window.LVMTrackingNotice?.enabled() ? 'lvm_notice_visit_v1' : CONSENT_VISIT_KEY;
  const stored = readStorage(window.sessionStorage, visitKey);
  if (stored) return { visitId: stored, storageFailed: false };
  if (!memoryVisitId) memoryVisitId = createToken('visit');
  const persisted = writeStorage(window.sessionStorage, visitKey, memoryVisitId);
  return { visitId: memoryVisitId, storageFailed: !persisted };
};

const chooseVariant = () => {
  const random = (() => {
    try {
      const values = new Uint32Array(1);
      crypto.getRandomValues(values);
      return values[0] / 4294967296;
    } catch (error) {
      return Math.random();
    }
  })();
  return random < 0.5 ? CONSENT_VARIANTS[0] : CONSENT_VARIANTS[1];
};

const readConsentExperiment = (now = Date.now(), { includeMemory = true } = {}) => {
  if (typeof window === 'undefined' || hasConsentMeasurementObjection()) return null;
  if (window.LVMTrackingNotice?.enabled()) return { variant: 'notice_default', storageFailed: false };
  const storedValue = readStorage(window.localStorage, CONSENT_EXPERIMENT_STORAGE_KEY);
  try {
    const parsed = JSON.parse(storedValue || 'null');
    if (
      parsed?.experimentId === CONSENT_EXPERIMENT_ID
      && CONSENT_VARIANTS.includes(parsed.variant)
      && Number(parsed.expiresAt) > now
    ) return { variant: parsed.variant, storageFailed: false };
  } catch (error) {
    // Invalid assignment state is removed below before a caller can reuse it.
  }
  if (storedValue !== null) removeStorage(window.localStorage, CONSENT_EXPERIMENT_STORAGE_KEY);
  if (memoryVariant && memoryVariantExpiresAt <= now) {
    memoryVariant = '';
    memoryVariantExpiresAt = 0;
  }
  return includeMemory && memoryVariant ? { variant: memoryVariant, storageFailed: true } : null;
};

export const getConsentExperiment = (now = Date.now()) => {
  const existing = readConsentExperiment(now, { includeMemory: false });
  if (existing || typeof window === 'undefined' || hasConsentMeasurementObjection()) return existing;
  const variant = memoryVariant || chooseVariant();
  memoryVariant = variant;
  memoryVariantExpiresAt = now + CONSENT_EXPERIMENT_MAX_AGE_MS;
  const persisted = writeStorage(window.localStorage, CONSENT_EXPERIMENT_STORAGE_KEY, JSON.stringify({
    experimentId: CONSENT_EXPERIMENT_ID,
    variant,
    assignedAt: now,
    expiresAt: now + CONSENT_EXPERIMENT_MAX_AGE_MS
  }));
  return { variant, storageFailed: !persisted };
};

const getSource = () => {
  try {
    return new URLSearchParams(window.location.search || '').get('utm_source') || '';
  } catch (error) {
    return '';
  }
};

const actionKey = (visitId, action) => `${visitId}:${action}`;

export const recordConsentFunnelAction = (action, {
  selection = 'not_applicable',
  beacon = false,
  allowWhenObjected = false,
  existingAssignmentOnly = false
} = {}) => {
  if (typeof window === 'undefined' || (!allowWhenObjected && hasConsentMeasurementObjection())) return false;
  const experiment = existingAssignmentOnly ? readConsentExperiment() : getConsentExperiment();
  if (!experiment) return false;
  const { visitId, storageFailed: visitStorageFailed } = getVisitId();
  const key = actionKey(visitId, action);
  if (action !== 'banner_impression' && memoryRecordedActions.has(key)) return false;
  memoryRecordedActions.add(key);
  const body = JSON.stringify({
    experimentId: CONSENT_EXPERIMENT_ID,
    variant: experiment.variant,
    surface: 'platform',
    site: 'lovevibesmusic.com',
    action,
    selection,
    visitId,
    eventId: createToken('event'),
    consentVersion: CONSENT_VERSION,
    landingPage: window.location.pathname || '/',
    source: getSource()
  });
  if (beacon && typeof navigator.sendBeacon === 'function') {
    return navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'application/json' }));
  }
  if (typeof window.fetch !== 'function') return false;
  window.fetch(ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    credentials: 'omit',
    keepalive: true
  }).catch(() => undefined);
  if ((experiment.storageFailed || visitStorageFailed) && action !== 'storage_failure') {
    recordConsentFunnelAction('storage_failure');
  }
  return true;
};

export const objectToConsentMeasurement = () => {
  if (typeof window === 'undefined') return;
  recordConsentFunnelAction('measurement_objection', {
    allowWhenObjected: true,
    existingAssignmentOnly: true
  });
  memoryMeasurementChoice = writeStorage(window.localStorage, CONSENT_MEASUREMENT_CHOICE_KEY, 'object')
    ? ''
    : 'object';
  removeStorage(window.localStorage, CONSENT_EXPERIMENT_STORAGE_KEY);
  removeStorage(window.sessionStorage, CONSENT_VISIT_KEY);
  removeStorage(window.sessionStorage, 'lvm_notice_visit_v1');
  memoryVisitId = '';
  memoryVariant = '';
  memoryVariantExpiresAt = 0;
  memoryRecordedActions.clear();
};

export const resumeConsentMeasurement = () => {
  if (typeof window === 'undefined') return;
  removeStorage(window.localStorage, CONSENT_MEASUREMENT_CHOICE_KEY);
  memoryMeasurementChoice = '';
  recordConsentFunnelAction('measurement_resume', { allowWhenObjected: true });
};

export const getConsentVariant = () => getConsentExperiment()?.variant || 'clear_choice';

export const isConsentJourneyDismissed = () => {
  if (typeof window === 'undefined') return false;
  return readStorage(window.sessionStorage, CONSENT_DISMISSED_KEY) === '1';
};

export const dismissConsentJourney = () => {
  if (typeof window === 'undefined') return;
  writeStorage(window.sessionStorage, CONSENT_DISMISSED_KEY, '1');
  recordConsentFunnelAction('dismiss_no_choice');
};

export const clearConsentJourneyDismissal = () => {
  if (typeof window === 'undefined') return;
  removeStorage(window.sessionStorage, CONSENT_DISMISSED_KEY);
};
